import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useCreateLocation,
  useDeleteLocation,
  useLocation,
  useLocationBrief,
  useLocationPosts,
  useLocations,
  useRunLocation,
  useUpdateLocation,
  useUpdateLocationBrief,
} from '../lib/hooks';
import { loadAuthState } from '../lib/auth-store';
import { getActiveMembership } from '../lib/role-access';
import { formatDateTimeEst, isSameEstDay } from '../lib/datetime';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  PageHeader,
  SkeletonList,
  StatusBadge,
  useSavedFlag,
  useToast,
} from '../components/ui';

function locationReference(row: any) {
  return row.id || row.clientId;
}

function normalizeTimeInputValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const first = value.split('-')[0]?.trim();
  return first || '';
}

type LocationTab = 'overview' | 'brief' | 'posts';

const EMPTY_NEW_LOCATION = {
  clientId: '',
  businessName: '',
  websiteUrl: '',
  city: '',
  country: 'US',
  mainServices: '',
  contactPhone: '',
  contactEmail: '',
  gbpAccountId: '',
  gbpLocationId: '',
  contentSourceUrls: '',
  postingFrequencyPerWeek: 2,
  preferredDays: 'tue,thu',
  preferredTimeWindow: '09:00',
  channelsEnabled: 'GBP',
  status: 'ACTIVE',
};

export function LocationsPage() {
  const auth = loadAuthState();
  const { toast } = useToast();
  const activeMembership = getActiveMembership(auth.user, auth.activeTenantId);
  const locationUser = activeMembership?.role === 'LOCATION_USER';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [tab, setTab] = useState<LocationTab>('overview');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const locationsQuery = useLocations({ search, status: status || undefined, page: 1, pageSize: 100 });
  const selectedRow = useMemo(
    () => locationsQuery.data?.rows.find((row: any) => locationReference(row) === selectedRef) || null,
    [locationsQuery.data, selectedRef],
  );

  const locationQuery = useLocation(selectedRef);
  const briefQuery = useLocationBrief(selectedRef);
  const postsQuery = useLocationPosts(selectedRef);

  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation(selectedRef || '');
  const deleteLocation = useDeleteLocation();
  const runLocation = useRunLocation(selectedRef || '');
  const updateBrief = useUpdateLocationBrief(selectedRef || '');

  const [overviewSaved, flashOverviewSaved] = useSavedFlag();
  const [briefSaved, flashBriefSaved] = useSavedFlag();

  const [createMode, setCreateMode] = useState(false);
  const [newLocation, setNewLocation] = useState({ ...EMPTY_NEW_LOCATION });

  const location = locationQuery.data || selectedRow;
  const brief = briefQuery.data;
  const rows = (locationsQuery.data?.rows || []) as any[];

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    try {
      const response = await createLocation.mutateAsync(newLocation);
      setCreateMode(false);
      setSelectedRef(response.location.id);
      setNewLocation({ ...EMPTY_NEW_LOCATION });
      toast({
        title: 'Location created',
        description: 'Fill in the content brief next so posts sound like this business.',
      });
    } catch (error) {
      setFormError((error as Error).message);
    }
  }

  async function handleSaveOverview(event: FormEvent) {
    event.preventDefault();
    if (!selectedRef || !location) {
      return;
    }

    const form = new FormData(event.currentTarget as HTMLFormElement);
    const payload = {
      businessName: String(form.get('businessName') || ''),
      websiteUrl: String(form.get('websiteUrl') || ''),
      city: String(form.get('city') || ''),
      country: String(form.get('country') || ''),
      mainServices: String(form.get('mainServices') || ''),
      targetKeywords: String(form.get('targetKeywords') || ''),
      contactPhone: String(form.get('contactPhone') || ''),
      preferredDays: String(form.get('preferredDays') || ''),
      preferredTimeWindow: String(form.get('preferredTimeWindow') || ''),
      postingFrequencyPerWeek: Number(form.get('postingFrequencyPerWeek') || 0),
      channelsEnabled: String(form.get('channelsEnabled') || ''),
      status: String(form.get('status') || ''),
      contactEmail: String(form.get('contactEmail') || ''),
      gbpAccountId: String(form.get('gbpAccountId') || ''),
      gbpLocationId: String(form.get('gbpLocationId') || ''),
      contentSourceUrls: String(form.get('contentSourceUrls') || ''),
      apifyActorId: String(form.get('apifyActorId') || ''),
      apifyQuery: String(form.get('apifyQuery') || ''),
      imageFolder: String(form.get('imageFolder') || ''),
    };

    try {
      await updateLocation.mutateAsync(payload);
      flashOverviewSaved();
      toast({ title: 'Location settings saved' });
    } catch (error) {
      toast({ variant: 'error', title: 'Could not save changes', description: (error as Error).message });
    }
  }

  async function handleSaveBrief(event: FormEvent) {
    event.preventDefault();
    if (!selectedRef) {
      return;
    }

    const form = new FormData(event.currentTarget as HTMLFormElement);
    try {
      await updateBrief.mutateAsync({
        goals: String(form.get('goals') || ''),
        targetAudience: String(form.get('targetAudience') || ''),
        valueProp: String(form.get('valueProp') || ''),
        mainServices: String(form.get('mainServices') || ''),
        preferredTone: String(form.get('preferredTone') || ''),
        topicsToEmphasize: String(form.get('topicsToEmphasize') || ''),
        topicsToAvoid: String(form.get('topicsToAvoid') || ''),
        localAreas: String(form.get('localAreas') || ''),
        hashtags: String(form.get('hashtags') || ''),
        offersPromos: String(form.get('offersPromos') || ''),
        examplePhrases: String(form.get('examplePhrases') || ''),
        notes: String(form.get('notes') || ''),
        languages: String(form.get('languages') || ''),
        competitors: String(form.get('competitors') || ''),
      });
      flashBriefSaved();
      toast({
        title: 'Content brief saved',
        description: 'Future posts will follow the updated brief.',
      });
    } catch (error) {
      toast({ variant: 'error', title: 'Could not save the brief', description: (error as Error).message });
    }
  }

  return (
    <>
      <PageHeader
        title="Locations"
        description="Each location is one Google Business Profile. Set how often it posts, and give it a brief so the content sounds right."
        actions={
          !locationUser && (
            <Button
              variant={createMode ? 'secondary' : 'primary'}
              icon={createMode ? undefined : <Icon.Plus />}
              onClick={() => setCreateMode((current) => !current)}
            >
              {createMode ? 'Close form' : 'Add location'}
            </Button>
          )
        }
      />

      {createMode && !locationUser && (
        <section className="card">
          <h3>Add a location manually</h3>
          <p className="card-sub">
            Tip: the fastest way to add locations is to import them from your connected Google account on the{' '}
            <Link to="/onboarding">Google connection</Link> page. Use this form when you want to set one up by hand.
          </p>
          <form className="grid" onSubmit={handleCreate}>
            <div className="form-grid">
              <Field label="Internal ID" hint="A short unique name for your own reference, e.g. “smith-roofing”.">
                <input
                  placeholder="smith-roofing"
                  value={newLocation.clientId}
                  onChange={(event) => setNewLocation((current) => ({ ...current, clientId: event.target.value }))}
                  required
                />
              </Field>
              <Field label="Business name">
                <input
                  placeholder="Smith Roofing Co."
                  value={newLocation.businessName}
                  onChange={(event) => setNewLocation((current) => ({ ...current, businessName: event.target.value }))}
                  required
                />
              </Field>
              <Field label="Website" hint="We read the website to learn about the business before writing posts.">
                <input
                  placeholder="https://example.com"
                  value={newLocation.websiteUrl}
                  onChange={(event) => setNewLocation((current) => ({ ...current, websiteUrl: event.target.value }))}
                />
              </Field>
              <Field label="City">
                <input
                  placeholder="Austin"
                  value={newLocation.city}
                  onChange={(event) => setNewLocation((current) => ({ ...current, city: event.target.value }))}
                />
              </Field>
              <Field label="Country">
                <input
                  placeholder="US"
                  value={newLocation.country}
                  onChange={(event) => setNewLocation((current) => ({ ...current, country: event.target.value }))}
                />
              </Field>
              <Field label="Main services" hint="Separate with commas. These appear in posts.">
                <input
                  placeholder="Roof repair, Gutter installation"
                  value={newLocation.mainServices}
                  onChange={(event) => setNewLocation((current) => ({ ...current, mainServices: event.target.value }))}
                />
              </Field>
              <Field label="Posts per week" hint="How many posts to publish each week (0 pauses posting).">
                <input
                  type="number"
                  min={0}
                  max={7}
                  value={newLocation.postingFrequencyPerWeek}
                  onChange={(event) =>
                    setNewLocation((current) => ({
                      ...current,
                      postingFrequencyPerWeek: Number(event.target.value || 0),
                    }))
                  }
                />
              </Field>
              <Field label="Posting days" hint="Days of the week, e.g. “tue,thu”. Leave empty for any day.">
                <input
                  placeholder="tue,thu"
                  value={newLocation.preferredDays}
                  onChange={(event) => setNewLocation((current) => ({ ...current, preferredDays: event.target.value }))}
                />
              </Field>
              <Field label="Posting time (US Eastern)">
                <input
                  type="time"
                  value={newLocation.preferredTimeWindow}
                  onChange={(event) =>
                    setNewLocation((current) => ({ ...current, preferredTimeWindow: event.target.value }))
                  }
                />
              </Field>
              <Field label="Client email" hint="Optional. We send them a copy of each published post.">
                <input
                  placeholder="owner@business.com"
                  value={newLocation.contactEmail}
                  onChange={(event) => setNewLocation((current) => ({ ...current, contactEmail: event.target.value }))}
                />
              </Field>
            </div>

            {formError && <InlineAlert variant="danger">{formError}</InlineAlert>}

            <div>
              <Button type="submit" loading={createLocation.isPending} loadingText="Creating…">
                Create location
              </Button>
            </div>
          </form>
        </section>
      )}

      <div className="split-layout split-layout-locations">
        <section className="card">
          <div className="inline" style={{ marginBottom: 12 }}>
            <input
              placeholder="Search by business or city…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ flex: 1, minWidth: 150 }}
            />
            <select value={status} onChange={(event) => setStatus(event.target.value)} style={{ width: 'auto' }}>
              <option value="">All</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Paused</option>
              <option value="DELETED">Archived</option>
            </select>
          </div>

          {locationsQuery.isLoading ? (
            <SkeletonList rows={4} height={92} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon.Pin size={20} />}
              title={search || status ? 'No locations match your filters' : 'No locations yet'}
              description={
                search || status
                  ? 'Try a different search term or clear the status filter.'
                  : 'Import locations from your connected Google account, or add one manually.'
              }
              action={
                !search && !status ? (
                  <Link to="/onboarding">
                    <Button variant="secondary" size="sm">Go to Google connection</Button>
                  </Link>
                ) : undefined
              }
            />
          ) : (
            <div className="location-list">
              {rows.map((row: any) => {
                const selected = locationReference(row) === selectedRef;
                const postedToday = isSameEstDay(row.lastPostedAt, new Date());
                const dueToday = !postedToday && isSameEstDay(row.nextScheduledAt, new Date());
                return (
                  <button
                    key={locationReference(row)}
                    className={`location-item ${selected ? 'selected' : ''}`}
                    onClick={() => setSelectedRef(locationReference(row))}
                  >
                    <div className="inline" style={{ justifyContent: 'space-between' }}>
                      <strong>{row.businessName}</strong>
                      <StatusBadge status={row.status} />
                    </div>
                    <span className="muted">
                      {row.city || 'No city set'}
                      {row.country ? `, ${row.country}` : ''} · {row.postingFrequencyPerWeek || 0} posts/week
                    </span>
                    <span className="muted">
                      {postedToday ? (
                        <>Posted today</>
                      ) : dueToday ? (
                        <>Due to post today</>
                      ) : row.nextScheduledAt ? (
                        <>Next post: {formatDateTimeEst(row.nextScheduledAt)}</>
                      ) : (
                        <>No upcoming post scheduled</>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="card">
          {!selectedRef || !location ? (
            <EmptyState
              icon={<Icon.Pin size={20} />}
              title="Select a location"
              description="Pick a location on the left to edit its schedule, brief, and post history."
            />
          ) : (
            <>
              <div className="inline" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2>{location.businessName}</h2>
                  <p className="muted" style={{ margin: '2px 0 0' }}>
                    {location.city || 'No city set'}
                    {location.country ? `, ${location.country}` : ''} · Posting times use US Eastern time
                  </p>
                </div>

                <div className="inline">
                  <Button
                    variant="secondary"
                    icon={<Icon.Play />}
                    onClick={() => {
                      if (!selectedRef) {
                        return;
                      }

                      runLocation.mutate(
                        {
                          reason: 'manual_run_from_locations',
                          force: true,
                        },
                        {
                          onSuccess: () => {
                            toast({
                              title: 'Post queued',
                              description:
                                'We are writing and publishing a post for this location now. Watch progress in Activity.',
                            });
                          },
                          onError: (error) => {
                            toast({
                              variant: 'error',
                              title: 'Could not start the post',
                              description: (error as Error).message,
                            });
                          },
                        },
                      );
                    }}
                    loading={runLocation.isPending}
                    loadingText="Queueing…"
                    disabled={location.status === 'DELETED'}
                  >
                    Publish a post now
                  </Button>
                  {!locationUser && (
                    <Button
                      variant="danger"
                      icon={<Icon.Archive />}
                      onClick={() => setConfirmArchive(true)}
                      disabled={deleteLocation.isPending || location.status === 'DELETED'}
                    >
                      Archive
                    </Button>
                  )}
                </div>
              </div>

              <ConfirmDialog
                open={confirmArchive}
                title={`Archive ${location.businessName}?`}
                body="Archiving stops all scheduled posting for this location. Its history is kept and you can reactivate it later by setting its status back to Active."
                confirmLabel="Archive location"
                loading={deleteLocation.isPending}
                onCancel={() => setConfirmArchive(false)}
                onConfirm={() => {
                  if (!selectedRef) {
                    return;
                  }

                  deleteLocation.mutate(selectedRef, {
                    onSuccess: () => {
                      setConfirmArchive(false);
                      setSelectedRef(null);
                      toast({ title: 'Location archived', description: 'Scheduled posting has been stopped.' });
                    },
                    onError: (error) => {
                      setConfirmArchive(false);
                      toast({ variant: 'error', title: 'Could not archive', description: (error as Error).message });
                    },
                  });
                }}
              />

              <div className="segmented" style={{ margin: '14px 0' }}>
                <button type="button" className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
                  Settings
                </button>
                <button type="button" className={tab === 'brief' ? 'active' : ''} onClick={() => setTab('brief')}>
                  Content brief
                </button>
                <button type="button" className={tab === 'posts' ? 'active' : ''} onClick={() => setTab('posts')}>
                  Post history
                </button>
              </div>

              {tab === 'overview' && (
                <form onSubmit={handleSaveOverview} className="grid">
                  <fieldset className="form-section">
                    <legend>Business details</legend>
                    <div className="form-grid">
                      <Field label="Business name">
                        <input name="businessName" defaultValue={location.businessName || ''} />
                      </Field>
                      <Field label="Website" hint="We read this site to keep posts accurate and on-brand.">
                        <input name="websiteUrl" defaultValue={location.websiteUrl || ''} placeholder="https://example.com" />
                      </Field>
                      <Field label="City">
                        <input name="city" defaultValue={location.city || ''} />
                      </Field>
                      <Field label="Country">
                        <input name="country" defaultValue={location.country || ''} placeholder="US" />
                      </Field>
                      <Field label="Main services" hint="Comma-separated. Mentioned naturally in posts.">
                        <input name="mainServices" defaultValue={location.mainServices || ''} />
                      </Field>
                      <Field label="Keywords to target" hint="Optional search phrases to weave into posts.">
                        <input name="targetKeywords" defaultValue={location.targetKeywords || ''} />
                      </Field>
                    </div>
                  </fieldset>

                  <fieldset className="form-section">
                    <legend>Posting schedule</legend>
                    <p className="form-section-sub">
                      Posts go out automatically on the days and time you choose. Set posts per week to 0 to pause.
                    </p>
                    <div className="form-grid">
                      <Field label="Posts per week">
                        <input
                          name="postingFrequencyPerWeek"
                          type="number"
                          min={0}
                          max={7}
                          defaultValue={location.postingFrequencyPerWeek || 0}
                        />
                      </Field>
                      <Field label="Posting days" hint="e.g. “mon,wed,fri”. Leave empty for any day.">
                        <input name="preferredDays" defaultValue={location.preferredDays || ''} placeholder="mon,wed,fri" />
                      </Field>
                      <Field label="Posting time (US Eastern)">
                        <input
                          name="preferredTimeWindow"
                          type="time"
                          defaultValue={normalizeTimeInputValue(location.preferredTimeWindow)}
                        />
                      </Field>
                      <Field label="Status" hint="Paused keeps everything but stops new posts.">
                        <select name="status" defaultValue={location.status || 'ACTIVE'}>
                          <option value="ACTIVE">Active</option>
                          <option value="INACTIVE">Paused</option>
                          <option value="DELETED">Archived</option>
                        </select>
                      </Field>
                    </div>
                  </fieldset>

                  <fieldset className="form-section">
                    <legend>Google Business Profile link</legend>
                    <p className="form-section-sub">
                      These two values identify exactly which profile we publish to. They are filled in automatically
                      when you import locations from your Google account.
                    </p>
                    <div className="form-grid">
                      <Field label="Google account ID">
                        <input name="gbpAccountId" defaultValue={location.gbpAccountId || ''} placeholder="accounts/123…" className="mono" />
                      </Field>
                      <Field label="Google location ID">
                        <input name="gbpLocationId" defaultValue={location.gbpLocationId || ''} placeholder="locations/456…" className="mono" />
                      </Field>
                      <Field label="Channels">
                        <input name="channelsEnabled" defaultValue={location.channelsEnabled || 'GBP'} />
                      </Field>
                    </div>
                  </fieldset>

                  <fieldset className="form-section">
                    <legend>Notifications &amp; extras</legend>
                    <div className="form-grid">
                      <Field label="Client email" hint="Gets a copy of every published post.">
                        <input name="contactEmail" defaultValue={location.contactEmail || ''} placeholder="owner@business.com" />
                      </Field>
                      <Field label="Client phone">
                        <input name="contactPhone" defaultValue={location.contactPhone || ''} placeholder="+1 555 555 5555" />
                      </Field>
                      <Field label="Extra content sources" hint="Optional links (social pages, blog) we can draw ideas from.">
                        <input name="contentSourceUrls" defaultValue={location.contentSourceUrls || ''} placeholder="https://facebook.com/…" />
                      </Field>
                      <Field label="Apify actor ID" hint="Optional — only if you use a custom content scraper.">
                        <input name="apifyActorId" defaultValue={location.apifyActorId || ''} />
                      </Field>
                      <Field label="Apify query" hint="Optional — search query for the custom scraper.">
                        <input name="apifyQuery" defaultValue={location.apifyQuery || ''} />
                      </Field>
                      <Field label="Image folder" hint="Optional reference to a folder of brand images.">
                        <input name="imageFolder" defaultValue={location.imageFolder || ''} />
                      </Field>
                    </div>
                  </fieldset>

                  <div>
                    <Button type="submit" loading={updateLocation.isPending} loadingText="Saving…" saved={overviewSaved} savedText="Saved">
                      Save settings
                    </Button>
                  </div>
                </form>
              )}

              {tab === 'brief' && (
                <form onSubmit={handleSaveBrief} className="grid">
                  <InlineAlert variant="info">
                    The brief teaches the writer what this business is about. The more you fill in, the more every post
                    sounds like it was written by the owner.
                  </InlineAlert>
                  <div className="form-grid">
                    <Field label="Goals" hint="What should the posts achieve? e.g. “More booked estimates”.">
                      <input name="goals" defaultValue={brief?.goals || ''} />
                    </Field>
                    <Field label="Target audience" hint="Who are we talking to? e.g. “Homeowners in north Austin”.">
                      <input name="targetAudience" defaultValue={brief?.targetAudience || ''} />
                    </Field>
                    <Field label="What makes them different" hint="The one-line reason customers choose this business.">
                      <input name="valueProp" defaultValue={brief?.valueProp || ''} />
                    </Field>
                    <Field label="Services to promote">
                      <input name="mainServices" defaultValue={brief?.mainServices || ''} />
                    </Field>
                    <Field label="Tone of voice" hint="e.g. “Friendly and down-to-earth” or “Premium and polished”.">
                      <input name="preferredTone" defaultValue={brief?.preferredTone || ''} />
                    </Field>
                    <Field label="Topics to emphasize">
                      <input name="topicsToEmphasize" defaultValue={brief?.topicsToEmphasize || ''} />
                    </Field>
                    <Field label="Topics to avoid" hint="Anything the posts should never mention.">
                      <input name="topicsToAvoid" defaultValue={brief?.topicsToAvoid || ''} />
                    </Field>
                    <Field label="Neighborhoods served" hint="Local areas to mention, e.g. “Round Rock, Cedar Park”.">
                      <input name="localAreas" defaultValue={brief?.localAreas || ''} />
                    </Field>
                    <Field label="Hashtags" hint="Optional. Used sparingly when they fit.">
                      <input name="hashtags" defaultValue={brief?.hashtags || ''} />
                    </Field>
                    <Field label="Current offers" hint="Only real, active promotions — we never invent discounts.">
                      <input name="offersPromos" defaultValue={brief?.offersPromos || ''} />
                    </Field>
                    <Field label="Example phrases" hint="Calls to action the owner likes, e.g. “Give us a call today”.">
                      <input name="examplePhrases" defaultValue={brief?.examplePhrases || ''} />
                    </Field>
                    <Field label="Anything else" hint="Extra context for the writer.">
                      <input name="notes" defaultValue={brief?.notes || ''} />
                    </Field>
                    <Field label="Post language" hint="e.g. “English” or “Spanish”.">
                      <input name="languages" defaultValue={brief?.languages || ''} placeholder="English" />
                    </Field>
                    <Field label="Competitors" hint="For awareness only — posts never mention competitors.">
                      <input name="competitors" defaultValue={brief?.competitors || ''} />
                    </Field>
                  </div>

                  <div>
                    <Button type="submit" loading={updateBrief.isPending} loadingText="Saving…" saved={briefSaved} savedText="Saved">
                      Save brief
                    </Button>
                  </div>
                </form>
              )}

              {tab === 'posts' && (
                (postsQuery.data || []).length === 0 ? (
                  <EmptyState
                    icon={<Icon.Activity size={20} />}
                    title="No posts published yet"
                    description="Once posts go out for this location — on schedule or with “Publish a post now” — they will appear here."
                  />
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Published</th>
                          <th>Status</th>
                          <th>Post</th>
                          <th>View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(postsQuery.data || []).map((post: any) => (
                          <tr key={post.id}>
                            <td className="mono">{formatDateTimeEst(post.createdAt)}</td>
                            <td>
                              <StatusBadge status={post.status} />
                            </td>
                            <td style={{ whiteSpace: 'normal', maxWidth: 420 }}>{post.postText?.slice(0, 180)}</td>
                            <td>
                              {post.searchUrl ? (
                                <a href={post.searchUrl} target="_blank" rel="noreferrer">
                                  Open on Google <Icon.External size={12} />
                                </a>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}
